import { useFormContext } from 'react-hook-form';
import {
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form.tsx';
import { Input } from '@/components/ui/input.tsx';
import { Checkbox } from '@/components/ui/checkbox.tsx';
import {
  DEVICE_TYPE_CODE_BY_NAME,
  TDevice,
} from '@/features/devices/schemas/devices.schemas.ts';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select.tsx';
import { useTranslation } from 'react-i18next';
import { useDeviceTypesQuery } from '@/features/devices/hooks/queries/use-device-types-query.ts';

export const DeviceEditDetailsTabForm = () => {
  const form = useFormContext<TDevice>();
  const { t } = useTranslation();
  const { data: deviceTypes } = useDeviceTypesQuery();

  return (
    <div className="flex flex-col gap-2">
      <FormField
        control={form.control}
        name="name"
        render={({ field }) => (
          <FormItem>
            <FormLabel>{t('devices.deviceName')}</FormLabel>
            <FormControl>
              <Input {...field} />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />
      <FormField
        control={form.control}
        name="sn"
        render={({ field }) => (
          <FormItem>
            <FormLabel>{t('devices.details.serialNumber')}</FormLabel>
            <FormControl>
              <Input {...field} />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />
      <FormField
        control={form.control}
        name="type"
        render={({ field }) => (
          <FormItem>
            <FormLabel>{t('devices.details.deviceType.title')}</FormLabel>
            <FormControl>
              <Select
                value={field.value}
                onValueChange={(value: 'Inverter' | 'Dongle') => {
                  field.onChange(value);
                  form.setValue('typeId', DEVICE_TYPE_CODE_BY_NAME[value]);
                }}
              >
                <FormControl>
                  <SelectTrigger className="w-full bg-white">
                    <SelectValue
                      placeholder={t('devices.details.deviceType.typePlaceholder')}
                    />
                  </SelectTrigger>
                </FormControl>
                <SelectContent>
                  {deviceTypes?.map((option) => (
                    <SelectItem key={option.value} value={option.label}>
                      {option.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />
      <FormField
        control={form.control}
        name="model"
        render={({ field }) => (
          <FormItem>
            <FormLabel>{t('devices.details.model')}</FormLabel>
            <FormControl>
              <Input {...field} value={field.value ?? ''} />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />
      <FormField
        control={form.control}
        name="supplier"
        render={({ field }) => (
          <FormItem>
            <FormLabel>{t('devices.details.supplier')}</FormLabel>
            <FormControl>
              <Input {...field} />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />
      <FormField
        control={form.control}
        name="showInDashboard"
        render={({ field }) => (
          <FormItem className="flex items-center gap-2 space-y-0 pt-2">
            <FormControl>
              <Checkbox checked={field.value} onCheckedChange={field.onChange} />
            </FormControl>
            <FormLabel>{t('devices.details.showInDashboard')}</FormLabel>
            <FormMessage />
          </FormItem>
        )}
      />

      <div className="flex flex-col gap-1">
        {(['plantId', 'deviceId'] as (keyof TDevice)[]).map((fieldName) => (
          <FormField
            key={fieldName}
            control={form.control}
            name={fieldName}
            render={() => (
              <FormItem>
                <FormMessage />
              </FormItem>
            )}
          />
        ))}
      </div>
    </div>
  );
};
